import { ReactNode } from "react";
import Toaster from "../DesignSystem/Toaster";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useRolePermissionContext, useRolePermissionsLoading, type RolePermissionContext } from "@Front/Stores/RolePermissionsStore";
import { useOfficeScopedPermissionCan } from "@Front/Hooks/useOfficeScopedPermissionCan";
import { AppRuleNames } from "@Front/Api/Entities/rule";
import { formatSplashMessage } from "@Front/Utils/formatters";
import { getNotarySplashChromeLabels, resolveNotarySplashChromeLocale } from "@Front/Utils/notarySplashChromeHelpers";
import { NotaryMissingContactEmailGate } from "@Front/Components/LayoutTemplates/NotaryMissingContactEmailGate";
import splashClasses from "@Front/Components/DesignSystem/Modal/SplashModalLayout/splashModalLayout.module.scss";
import BrandHead from "@Front/Components/Layouts/Common/BrandHead";
import { useLogLayoutContextSource } from "@Front/Hooks/useLogLayoutContextSource";
import {
	isOverlayBlockedForPath,
	resolveRouteContextFamily,
} from "@Front/Utils/routeContextRegistry";

type DefaultLayoutProps = { children: ReactNode };

function SplashNotice(): React.JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const message = process.env['NEXT_PUBLIC_SPLASH_MESSAGE'] || "";
  const maxDisplaysStr = process.env['NEXT_PUBLIC_SPLASH_MAX_DISPLAYS'] || "10";
  const maxDisplays = parseInt(maxDisplaysStr, 10) || 10;
  const splashId = process.env['NEXT_PUBLIC_SPLASH_ID'] || "splash_v1";
  const router = useRouter();
  const permissionContext = useRolePermissionContext();
	const permissionsLoading = useRolePermissionsLoading();
  const officeScopedCan = useOfficeScopedPermissionCan();
  const hasOfficeFoldersRead = officeScopedCan(AppRuleNames.officeFolders, "read");

  useSplashVisibility({
    message,
    maxDisplays,
    splashId,
    pathname: router?.pathname,
    permissionsLoading,
    permissionContext,
    hasOfficeFoldersRead,
    setVisible,
  });

  const handleClose = useSplashCloseHandler(splashId, setVisible);

  if (!visible) return null;

  const chrome = getNotarySplashChromeLabels(resolveNotarySplashChromeLocale());
  return renderSplashModal(formatSplashMessage(message), handleClose, chrome);
}

type UseSplashVisibilityParams = {
  message: string;
  maxDisplays: number;
  splashId: string;
  pathname: string | undefined;
  permissionsLoading: boolean;
  permissionContext: RolePermissionContext;
  hasOfficeFoldersRead: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

function splashPathBlocksDisplay(pathname: string | undefined): boolean {
  const path = pathname || "";
  const routeContextFamily = resolveRouteContextFamily(path);
  const isBlockedByStrategy = isOverlayBlockedForPath({
    overlay: "splash_notice",
    pathname: path,
    routeContextFamily,
  });
  const isFoldersPage = path === "/folder" || path === "/folder/";
  return isBlockedByStrategy || !isFoldersPage;
}

function readSplashDisplayCount(storageKey: string): number {
  const displayCountStr = typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
  return displayCountStr ? parseInt(displayCountStr, 10) : 0;
}

function useSplashVisibility(params: UseSplashVisibilityParams): void {
  const { message, maxDisplays, splashId, pathname, permissionsLoading, permissionContext, hasOfficeFoldersRead, setVisible } = params;
  useEffect(() => {
    if (!message || permissionsLoading) {
      setVisible(false);
      return;
    }

    if (!hasOfficeFoldersRead || !permissionContext || splashPathBlocksDisplay(pathname)) {
      setVisible(false);
      return;
    }

    const sessionKey = `splash_shown_session_${splashId}`;
    const shownThisSession = typeof window !== "undefined" ? sessionStorage.getItem(sessionKey) : null;

    if (shownThisSession === "true") {
      setVisible(false);
      return;
    }

    const storageKey = `splash_displays_${splashId}`;
    if (readSplashDisplayCount(storageKey) >= maxDisplays) {
      setVisible(false);
      return;
    }

    if (typeof window !== "undefined") {
      sessionStorage.setItem(sessionKey, "true");
    }

    setVisible(true);
  }, [message, maxDisplays, splashId, pathname, permissionsLoading, permissionContext, hasOfficeFoldersRead, setVisible]);
}

function useSplashCloseHandler(
  splashId: string,
  setVisible: React.Dispatch<React.SetStateAction<boolean>>,
): () => void {
  return () => {
    setVisible(false);
    if (typeof window !== 'undefined') {
      const storageKey = `splash_displays_${splashId}`;
      const displayCountStr = localStorage.getItem(storageKey);
      const displayCount = displayCountStr ? parseInt(displayCountStr, 10) : 0;
      localStorage.setItem(storageKey, String(displayCount + 1));
    }
  };
}

function SplashModalCard({
  message,
  handleClose,
  title,
  closeButton,
}: {
  message: string;
  handleClose: () => void;
  title: string;
  closeButton: string;
}): React.JSX.Element {
  return (
    <div className={splashClasses["card"]}>
      <h2 className={splashClasses["title"]}>{title}</h2>
      <div className={splashClasses["scrollArea"]}>
        <p className={splashClasses["message"]}>{message}</p>
      </div>
      <div className={splashClasses["footer"]}>
        <button type="button" className={splashClasses["closeButton"]} onClick={handleClose}>
          {closeButton}
        </button>
      </div>
    </div>
  );
}

function renderSplashModal(
  message: string,
  handleClose: () => void,
  chrome: { title: string; closeButton: string },
): React.JSX.Element {
  return (
    <div className={splashClasses["overlay"]}>
      <SplashModalCard message={message} handleClose={handleClose} title={chrome.title} closeButton={chrome.closeButton} />
    </div>
  );
}

export const DefaultLayout = ({ children }: DefaultLayoutProps): React.JSX.Element => {
	useLogLayoutContextSource("DefaultLayout");
	return (
		<>
			<BrandHead />
			<main>
				<SplashNotice />
				<NotaryMissingContactEmailGate />
				{children}
				<Toaster />
			</main>
		</>
	);
};
